import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../api/problem";
import { formatDateTime } from "../../i18n/format";
import { Button } from "../../ui/Button";
import { ConfirmDialog } from "../ops/shared/ConfirmDialog";
import { useOrg } from "../org/OrgProvider";
import { claimObjectionsQuery, decideClaimObjection, type ClaimObjection } from "./queries";
import { hasOrganisationSection } from "./roles";

type T = (key: string, options?: Record<string, unknown>) => string;

/** What a decision's refusal says (WEB-OBJ-03). */
export function objectionError(error: unknown, t: T): string {
  if (error instanceof ApiError && error.code === "window_closed") return t("organisation.objections.closed");
  return error instanceof Error ? error.message : t("organisation.error.unknown");
}

/** The device as the card names it: its serial, else its id. */
export function objectionDevice(o: ClaimObjection): string {
  return o.serial ?? o.device_id;
}

/** The open windows of the current organisation, asked only by its Owners and Admins (WEB-OBJ-01, plan risk). */
export function useClaimObjections() {
  const { orgId, current } = useOrg();
  const allowed = hasOrganisationSection(current);
  return useQuery({ ...claimObjectionsQuery(orgId ?? ""), enabled: allowed && !!orgId });
}

/**
 * A device the organisation released was claimed again by its label (P-191, claim-objections WEB-OBJ-01 to -03):
 * one line per window, with Object (asks first) and This is fine (at once). Nothing when there is none.
 */
export function ClaimObjectionsCard() {
  const { t, i18n } = useTranslation();
  const { orgId } = useOrg();
  const qc = useQueryClient();
  const list = useClaimObjections();
  const [asking, setAsking] = useState<ClaimObjection>();
  const [said, setSaid] = useState<string>();
  const decide = useMutation({
    mutationFn: ({ o, decision }: { o: ClaimObjection; decision: "object" | "approve" }) =>
      decideClaimObjection(o.id, decision),
    onSuccess: (_, { o, decision }) => {
      setSaid(t(`organisation.objections.done.${decision}`, { device: objectionDevice(o) }));
      setTimeout(() => setSaid(undefined), 6000);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: claimObjectionsQuery(orgId ?? "").queryKey }),
  });
  const items = (list.data ?? []).filter((o) => o.state === "open");
  if (items.length === 0 && !said && !decide.error) return null;
  return (
    <section aria-labelledby="claim-objections-title" className="rounded-md border border-amber-300 bg-amber-50 p-4">
      <h2 id="claim-objections-title" className="text-base font-semibold text-gray-900">
        {t("organisation.objections.title")}
      </h2>
      {said && (
        <p role="status" className="mt-2 text-sm text-gray-900">
          {said}
        </p>
      )}
      {decide.error && (
        <p role="alert" className="mt-2 text-sm text-red-800">
          {objectionError(decide.error, t)}
        </p>
      )}
      <ul className="mt-2 space-y-3">
        {items.map((o) => (
          <li key={o.id} className="flex flex-wrap items-center gap-3 text-sm text-gray-900">
            <span className="min-w-0 flex-1">
              {t("organisation.objections.line", {
                device: objectionDevice(o),
                until: formatDateTime(o.closes_at, i18n.language),
              })}
            </span>
            <Button variant="secondary" disabled={decide.isPending} onClick={() => setAsking(o)}>
              {t("organisation.objections.object")}
            </Button>
            <Button
              variant="secondary"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ o, decision: "approve" })}
            >
              {t("organisation.objections.approve")}
            </Button>
          </li>
        ))}
      </ul>
      <ConfirmDialog
        open={!!asking}
        title={t("organisation.objections.confirmTitle")}
        confirmLabel={t("organisation.objections.object")}
        onClose={() => setAsking(undefined)}
        onConfirm={() => {
          if (asking) decide.mutate({ o: asking, decision: "object" });
          setAsking(undefined);
        }}
      >
        <p>{t("organisation.objections.confirm", { device: asking ? objectionDevice(asking) : "" })}</p>
      </ConfirmDialog>
    </section>
  );
}

/** One line on Home while a window is open, for Owners and Admins (WEB-OBJ-04). */
export function ClaimObjectionsNotice() {
  const { t } = useTranslation();
  const list = useClaimObjections();
  if (!(list.data ?? []).some((o) => o.state === "open")) return null;
  return (
    <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-gray-900">
      {t("organisation.objections.notice")}{" "}
      <Link to="/organisation" className="font-medium text-brand underline">
        {t("organisation.objections.review")}
      </Link>
    </p>
  );
}
